import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useEventManager } from '../events/hooks/useEventManager';
import { useSimpleEventOrganization } from '../events/hooks/useSimpleEventOrganization';
import { useSettings } from '../../contexts/settingsContext';
import { useDialogState } from '../events/hooks/useDialogState';
import { useHistoryDrawer } from '../../contexts/historyDrawerContext';
import HistoryDrawer from '../events/list/HistoryDrawer';
import HistoryDialogs from '../events/dialogs/HistoryDialogs';

/**
 * HistoryManager - Main component for event history management
 * Refactored for better maintainability and separation of concerns
 */
export default function HistoryManager({ open, onClose }) {
  // Event management
  const {
    allEvents,
    openAssignments,
    loading,
    loadingMore,
    hasMore,
    error,
    success,
    pageSize,
    loadAllEvents,
    loadMoreEvents,
    changePageSize,
    createNewAssignment,
    toggleAssignment,
    createNewNote,
    updateEventDescription,
    deleteEventById,
    clearError,
    clearSuccess
  } = useEventManager();

  // Share open assignments with global context for PersonCard
  const { updateOpenAssignments, pendingMatchEvents, removePendingMatchEvent } = useHistoryDrawer();
  useEffect(() => {
    updateOpenAssignments(openAssignments);
  }, [openAssignments, updateOpenAssignments]);

  // Merge pending match events with real events
  const mergedEvents = React.useMemo(() => {
    // Remove pending events that now exist as real events
    // 5000ms window: Treat events as duplicates if they have same person/eventId and occur within 5 seconds
    const DUPLICATE_DETECTION_WINDOW_MS = 5000;

    const validPending = pendingMatchEvents.filter(pe => {
      // For group events, check by eventId
      if (pe.eventType === 'group_match') {
        return !allEvents.some(e => e.eventId === pe.eventId);
      }

      // For match events, check by person name AND capturedImageUrl (more precise than just time)
      // This ensures we only remove pending events when the EXACT same match loads from the server
      const hasDuplicate = allEvents.some(e =>
        e.eventType === 'match' &&
        e.metadata?.personName === pe.metadata?.personName &&
        e.metadata?.capturedImageUrl === pe.metadata?.capturedImageUrl &&
        Math.abs(new Date(e.createdAt).getTime() - new Date(pe.createdAt).getTime()) < DUPLICATE_DETECTION_WINDOW_MS
      );
      return !hasDuplicate;
    });

    return [...validPending, ...allEvents].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [allEvents, pendingMatchEvents]);

  // Clean up pending events that are now duplicates (in useEffect, not during render)
  useEffect(() => {
    const DUPLICATE_DETECTION_WINDOW_MS = 5000;
    pendingMatchEvents.forEach(pe => {
      let hasDuplicate = false;

      // For group events, check by eventId
      if (pe.eventType === 'group_match') {
        hasDuplicate = allEvents.some(e => e.eventId === pe.eventId);
      } else {
        // For match events, check by person name AND capturedImageUrl (more precise)
        hasDuplicate = allEvents.some(e =>
          e.eventType === 'match' &&
          e.metadata?.personName === pe.metadata?.personName &&
          e.metadata?.capturedImageUrl === pe.metadata?.capturedImageUrl &&
          Math.abs(new Date(e.createdAt).getTime() - new Date(pe.createdAt).getTime()) < DUPLICATE_DETECTION_WINDOW_MS
        );
      }

      if (hasDuplicate) {
        removePendingMatchEvent(pe.eventId);
      }
    });
  }, [allEvents, pendingMatchEvents, removePendingMatchEvent]);

  // Settings management (from global context)
  const { deleteConfirmation, deleteConfirmationRef } = useSettings();

  // Dialog state management
  const {
    dialogs,
    formState,
    openDialog,
    closeDialog,
    updateForm,
    closeAndResetDialog
  } = useDialogState();

  // Track expanded assignments and group matches
  const [expandedAssignments, setExpandedAssignments] = useState(new Set());
  const [expandedGroupMatches, setExpandedGroupMatches] = useState(new Set());

  // ALWAYS keep open assignment expanded - it should never be collapsed
  useEffect(() => {
    const openAssignment = mergedEvents.find(e => e.eventType === 'assignment' && e.status === 'open');
    const openAssignmentId = openAssignment?.eventId;

    if (openAssignmentId) {
      setExpandedAssignments(prev => {
        // Only update if not already expanded
        if (prev.has(openAssignmentId)) return prev;
        const newSet = new Set(prev);
        newSet.add(openAssignmentId);
        return newSet;
      });
    }
  }, [mergedEvents]); // Run whenever events change to ensure open assignment stays expanded

  // Simple event organization - just get top-level events
  // Components handle rendering their own children
  const topLevelEvents = useSimpleEventOrganization(mergedEvents);

  // Stable ref for deleteEventById
  const deleteEventByIdRef = useRef(deleteEventById);
  useEffect(() => {
    deleteEventByIdRef.current = deleteEventById;
  }, [deleteEventById]);

  // Stable ref for loadAllEvents to avoid re-running effects
  const loadAllEventsRef = useRef(loadAllEvents);
  useEffect(() => {
    loadAllEventsRef.current = loadAllEvents;
  }, [loadAllEvents]);

  // Load events when drawer opens (only if never loaded)
  const hasLoadedOnce = useRef(false);
  useEffect(() => {
    if (open && !hasLoadedOnce.current) {
      loadAllEventsRef.current();
      hasLoadedOnce.current = true;
    }
  }, [open]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Handle assignment creation
   */
  const handleCreateAssignment = useCallback(async () => {
    const success = await createNewAssignment(formState.newAssignmentDesc);
    if (success) {
      closeAndResetDialog('createAssignment', 'newAssignmentDesc');
    }
  }, [createNewAssignment, formState.newAssignmentDesc, closeAndResetDialog]);

  /**
   * Handle assignment completion (toggle to closed status)
   */
  const handleCompleteAssignment = useCallback(async () => {
    if (!formState.selectedAssignment) return;
    const success = await toggleAssignment(formState.selectedAssignment.eventId, 'closed');
    if (success) {
      closeAndResetDialog('completeAssignment', 'selectedAssignment');
    }
  }, [toggleAssignment, formState.selectedAssignment, closeAndResetDialog]);

  /**
   * Handle note creation
   */
  const handleCreateNote = useCallback(async () => {
    const success = await createNewNote(formState.noteText);
    if (success) {
      closeAndResetDialog('createNote', 'noteText');
    }
  }, [createNewNote, formState.noteText, closeAndResetDialog]);

  /**
   * Handle event click - opens action dialog
   */
  const handleEventClick = useCallback((event) => {
    updateForm('selectedEvent', event);
    openDialog('eventAction');
  }, [updateForm, openDialog]);

  /**
   * Handle edit button click from action dialog
   */
  const handleEditClick = useCallback(() => {
    updateForm('editDescription', formState.selectedEvent.description);
    closeDialog('eventAction');
    openDialog('editEvent');
  }, [formState.selectedEvent, updateForm, closeDialog, openDialog]);

  /**
   * Handle edit save
   */
  const handleEditSave = useCallback(async () => {
    const success = await updateEventDescription(
      formState.selectedEvent.eventId,
      formState.editDescription
    );
    if (success) {
      closeAndResetDialog('editEvent', 'selectedEvent', 'editDescription');
    }
  }, [updateEventDescription, formState.selectedEvent, formState.editDescription, closeAndResetDialog]);

  /**
   * Handle delete from action dialog
   */
  const handleDeleteFromAction = useCallback(async () => {
    closeDialog('eventAction');
    const eventType = formState.selectedEvent?.eventType;

    if (deleteConfirmation[eventType]) {
      // Show confirmation dialog
      updateForm('eventToDelete', formState.selectedEvent);
      openDialog('deleteEvent');
    } else {
      // Delete immediately without confirmation
      await deleteEventById(formState.selectedEvent);
      updateForm('selectedEvent', null);
    }
  }, [
    closeDialog,
    formState.selectedEvent,
    deleteConfirmation,
    updateForm,
    openDialog,
    deleteEventById
  ]);

  /**
   * Handle delete event confirmation
   */
  const handleDeleteEvent = useCallback(async () => {
    const success = await deleteEventById(formState.eventToDelete);
    if (success) {
      closeAndResetDialog('deleteEvent', 'eventToDelete');
    }
  }, [deleteEventById, formState.eventToDelete, closeAndResetDialog]);


  /**
   * Handle delete click from list item (stable callback using refs)
   */
  const handleDeleteClick = useCallback(async (event) => {
    const eventType = event?.eventType;

    if (deleteConfirmationRef.current[eventType]) {
      // Show confirmation dialog
      updateForm('eventToDelete', event);
      openDialog('deleteEvent');
    } else {
      // Delete immediately without confirmation
      await deleteEventByIdRef.current(event);
    }
  }, [deleteConfirmationRef, updateForm, openDialog]);

  /**
   * Handle assignment expand/collapse toggle
   * IMPORTANT: Open assignments (status='open') cannot be collapsed - they must always be expanded
   */
  const handleToggleAssignment = useCallback((eventId) => {
    // Check if this is an open assignment
    const assignment = mergedEvents.find(e => e.eventId === eventId);
    const isOpenAssignment = assignment?.eventType === 'assignment' && assignment?.status === 'open';

    // Don't allow collapsing open assignments
    if (isOpenAssignment) {
      return;
    }

    setExpandedAssignments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  }, [mergedEvents]);

  /**
   * Handle group match expand/collapse toggle
   */
  const handleToggleGroupMatch = useCallback((eventId) => {
    setExpandedGroupMatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  }, []);

  /**
   * Handle manual refresh
   */
  const handleRefresh = useCallback(() => {
    loadAllEvents();
  }, [loadAllEvents]);

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback((newSize) => {
    changePageSize(newSize);
    // Trigger reload after page size changes
    setTimeout(() => loadAllEvents(), 0);
  }, [changePageSize, loadAllEvents]);

  // ============================================================================
  // Dialog Close Handlers
  // ============================================================================

  const dialogCloseHandlers = {
    createNote: () => closeDialog('createNote'),
    createAssignment: () => closeDialog('createAssignment'),
    completeAssignment: () => closeDialog('completeAssignment'),
    deleteEvent: () => closeDialog('deleteEvent'),
    eventAction: () => closeDialog('eventAction'),
    editEvent: () => closeDialog('editEvent')
  };

  // ============================================================================
  // Dialog Action Handlers
  // ============================================================================

  const dialogActionHandlers = {
    onNoteTextChange: (value) => updateForm('noteText', value),
    onAssignmentDescChange: (value) => updateForm('newAssignmentDesc', value),
    onSelectAssignment: (value) => updateForm('selectedAssignment', value),
    onEditDescChange: (value) => updateForm('editDescription', value),
    onCreateNote: handleCreateNote,
    onCreateAssignment: handleCreateAssignment,
    onCompleteAssignment: handleCompleteAssignment,
    onDeleteEvent: handleDeleteEvent,
    onEditClick: handleEditClick,
    onDeleteFromAction: handleDeleteFromAction,
    onEditSave: handleEditSave
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <>
      <HistoryDrawer
        open={open}
        onClose={onClose}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        error={error}
        success={success}
        clearError={clearError}
        clearSuccess={clearSuccess}
        topLevelEvents={topLevelEvents}
        allEvents={mergedEvents}
        expandedGroupMatches={expandedGroupMatches}
        openAssignments={openAssignments}
        onEventClick={handleEventClick}
        onDeleteClick={handleDeleteClick}
        onToggleGroupMatch={handleToggleGroupMatch}
        onToggleAssignmentStatus={toggleAssignment}
        onRefresh={handleRefresh}
        onLoadMore={loadMoreEvents}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        onOpenNoteDialog={() => openDialog('createNote')}
        onOpenCreateDialog={() => openDialog('createAssignment')}
        onOpenCompleteDialog={() => openDialog('completeAssignment')}
      />

      <HistoryDialogs
        dialogs={dialogs}
        formState={formState}
        loading={loading}
        allEvents={allEvents}
        openAssignments={openAssignments}
        handlers={dialogActionHandlers}
        onClose={dialogCloseHandlers}
      />
    </>
  );
}
